import React, { useState, useRef, useEffect } from 'react';
import { FaChevronDown, FaSearch, FaTimes, FaPlus } from 'react-icons/fa';

const CategorySelector = ({ 
  categories = [], 
  selectedCategoryId, 
  onCategoryChange, 
  placeholder = "选择分类",
  allowEmpty = true 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCategories, setFilteredCategories] = useState(categories);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Filter categories based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredCategories(categories);
    } else {
      const filtered = categories.filter(category =>
        category.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCategories(filtered);
    }
  }, [categories, searchTerm]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const selectedCategory = categories.find(cat => cat.id === selectedCategoryId);

  const handleCategorySelect = (categoryId) => {
    onCategoryChange(categoryId);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClearSelection = (e) => {
    e.stopPropagation();
    onCategoryChange('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className="w-full p-2 border rounded cursor-pointer bg-white flex items-center justify-between hover:border-gray-400 focus-within:border-red-700 focus-within:ring-1 focus-within:ring-red-700"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center flex-1">
          {selectedCategory ? (
            <span className="text-gray-900">{selectedCategory.name}</span>
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {selectedCategory && allowEmpty && (
            <button
              type="button"
              onClick={handleClearSelection}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              <FaTimes size={12} />
            </button>
          )}
          <FaChevronDown 
            className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            size={14}
          />
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-hidden">
          {/* Search input */}
          <div className="p-2 border-b">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="搜索分类..."
                className="w-full pl-8 pr-3 py-2 text-sm border rounded focus:outline-none focus:border-red-700"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          {/* Categories list */}
          <div className="max-h-40 overflow-y-auto">
            {allowEmpty && (
              <div
                className={`px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm ${
                  !selectedCategoryId ? 'bg-red-50 text-red-700' : 'text-gray-700'
                }`}
                onClick={() => handleCategorySelect('')}
              >
                不选择分类
              </div>
            )}
            
            {filteredCategories.length > 0 ? (
              filteredCategories.map((category) => (
                <div
                  key={category.id}
                  className={`px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm ${
                    selectedCategoryId === category.id ? 'bg-red-50 text-red-700' : 'text-gray-700'
                  }`}
                  onClick={() => handleCategorySelect(category.id)}
                >
                  {category.name}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500">
                {searchTerm ? '未找到匹配的分类' : '暂无分类'}
              </div>
            )}
          </div>

          {/* Add new category hint */}
          {searchTerm && filteredCategories.length === 0 && (
            <div className="border-t p-2">
              <div className="text-xs text-gray-500 flex items-center">
                <FaPlus className="mr-1" />
                未找到 "{searchTerm}" 分类，可前往分类管理添加
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CategorySelector;